import React, { useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FeedbackState } from "../components/FeedbackState";
import { InfoGroupSelected } from "./InfoGroupSelected";
import { useGroup } from "../context/groupContext";
import { useBets } from "../context/betsContext";
import { useAppContext } from "../context";

function SelectGroup() {
	const {
		getInfoGroupsSubscribed,
		infoGroupsUserSubscribed,
		loadingGroupsSubscribed,
		groupsSubscribedError,
	} = useGroup();

	const { setLoadingUpdateLegueBets } = useBets();

	const {
		currentUniqueIdentifierSelected,
		setCurrentUniqueIdentifierSelected,
		toastMessage,
	} = useAppContext();

	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const groupIdFromQuery = searchParams.get("g") || "";
	const getInfoGroupsSubscribedRef = useRef(getInfoGroupsSubscribed);
	const subscribedGroups = useMemo(
		() => infoGroupsUserSubscribed.Result || [],
		[infoGroupsUserSubscribed.Result]
	);
	const hasSubscribedGroups = subscribedGroups.length > 0;
	const groupIds = useMemo(
		() => subscribedGroups.map((group) => group.uniqueIdentifier),
		[subscribedGroups]
	);

	useEffect(() => {
		getInfoGroupsSubscribedRef.current = getInfoGroupsSubscribed;
	}, [getInfoGroupsSubscribed]);

	useEffect(() => {
		getInfoGroupsSubscribedRef.current();
	}, []);

	useEffect(() => {
		if (loadingGroupsSubscribed) return;

		if (!hasSubscribedGroups) {
			if (currentUniqueIdentifierSelected) {
				setCurrentUniqueIdentifierSelected("");
			}

			if (groupIdFromQuery) {
				setSearchParams({}, { replace: true });
			}

			return;
		}

		const firstSubscribedGroupId = subscribedGroups[0].uniqueIdentifier;
		const hasValidQueryGroup = groupIds.includes(groupIdFromQuery);
		const hasValidCurrentGroup = groupIds.includes(
			currentUniqueIdentifierSelected
		);
		const nextSelectedGroup = hasValidQueryGroup
			? groupIdFromQuery
			: hasValidCurrentGroup
				? currentUniqueIdentifierSelected
				: firstSubscribedGroupId;

		if (nextSelectedGroup !== currentUniqueIdentifierSelected) {
			setCurrentUniqueIdentifierSelected(nextSelectedGroup);
		}

		if (groupIdFromQuery !== nextSelectedGroup) {
			setSearchParams({ g: nextSelectedGroup }, { replace: true });
		}
	}, [
		currentUniqueIdentifierSelected,
		groupIdFromQuery,
		groupIds,
		hasSubscribedGroups,
		loadingGroupsSubscribed,
		setSearchParams,
		setCurrentUniqueIdentifierSelected,
		subscribedGroups,
	]);

	const selectedGroup = useMemo(
		() =>
			subscribedGroups.find(
				(item) => item.uniqueIdentifier === currentUniqueIdentifierSelected
			) || null,
		[subscribedGroups, currentUniqueIdentifierSelected]
	);

	const selectedLeagueName =
		selectedGroup?.groupLeagues?.[0]?.league?.name || "Campeonato";
	const fixtureBetTypeLabel =
		selectedGroup?.fixtureBetTypeName ||
		(selectedGroup?.fixtureBetTypeId === 2 ? "Marcador" : "Ganador");
	const allowLeagueBets =
		selectedGroup?.groupLeagues?.some((league) => league.allowLeagueBets) ||
		false;

	const handleOnchangeToSelectedGroup = (event) => {
		const nextGroupId = event.target.value;
		setLoadingUpdateLegueBets(true);
		setCurrentUniqueIdentifierSelected(nextGroupId);
		navigate(`/groups?g=${nextGroupId}`);
	};

	const handleCopyAccessCode = async () => {
		if (!selectedGroup?.accessCode || !navigator?.clipboard) return;

		try {
			await navigator.clipboard.writeText(selectedGroup.accessCode);
			toastMessage("Copia exitosa del codigo de acceso", "info");
		} catch (error) {
			toastMessage("No fue posible copiar el codigo de acceso", "error");
		}
	};

	return (
		<div className='container-fluid mt-4 groups-page-shell'>
			<section className='groups-page-hero card border-0'>
				<div className='card-body'>
					<div className='groups-page-hero-layout'>
						<div className='groups-page-hero-copy'>
							<p className='groups-page-eyebrow mb-2'>Tus grupos</p>
							<h1 className='groups-page-title mb-3'>
								{selectedGroup ? selectedGroup.name : "Administra tus grupos"}
							</h1>
							<p className='groups-page-subtitle mb-0'>
								Prioriza tus pronosticos, revisa la tabla de posiciones y cambia
								de grupo sin perder el contexto de la jornada.
							</p>
						</div>

						<div className='groups-page-selector-panel'>
							<label htmlFor='select-group' className='form-label mb-2'>
								<strong>Grupo activo</strong>
							</label>
							{loadingGroupsSubscribed ? (
								<FeedbackState
									variant='loading'
									title='Cargando tus grupos'
									description='Estamos recuperando tus espacios activos para que puedas seguir pronosticando.'
									compact
								/>
							) : groupsSubscribedError ? (
								<FeedbackState
									variant='error'
									title='No pudimos cargar tus grupos'
									description={groupsSubscribedError}
									actionLabel='Reintentar'
									onAction={getInfoGroupsSubscribed}
									compact
								/>
							) : !hasSubscribedGroups ? (
								<div className='groups-page-selector-empty'>
									<p className='mb-3'>
										No tienes grupos activos todavia. Crea uno nuevo o unete con
										un codigo para empezar a jugar.
									</p>
									<div className='groups-page-empty-actions'>
										<Link
											to='/create-new-group'
											className='btn btn-light btn-sm w-100'
										>
											Crear un grupo
										</Link>
										<Link
											to='/groups/join'
											className='btn btn-outline-light btn-sm w-100 mt-2'
										>
											Unirme con codigo
										</Link>
									</div>
								</div>
							) : (
								<select
									className='form-select input-select-group-fixture-bets'
									id='select-group'
									aria-label='Seleccionar grupo'
									onChange={handleOnchangeToSelectedGroup}
									value={currentUniqueIdentifierSelected || ""}
								>
									{subscribedGroups.map((item) => (
										<option
											key={item.uniqueIdentifier}
											value={item.uniqueIdentifier}
										>
											{item.name}
										</option>
									))}
								</select>
							)}
						</div>
					</div>

					{selectedGroup && (
						<div className='groups-page-meta mt-4'>
							<span className='groups-page-chip'>{selectedLeagueName}</span>
							<span className='groups-page-chip'>
								Apuesta: {fixtureBetTypeLabel}
							</span>
							<span className='groups-page-chip'>
								{allowLeagueBets
									? "Pronosticos de campeonato activos"
									: "Sin pronosticos de campeonato"}
							</span>
							{selectedGroup.isGroupAdmin && (
								<button
									type='button'
									className='groups-page-chip groups-page-chip-button'
									onClick={handleCopyAccessCode}
								>
									Codigo: {selectedGroup.accessCode}
								</button>
							)}
						</div>
					)}
				</div>
			</section>

			{selectedGroup ? (
				<InfoGroupSelected
					currentUniqueIdentifierSelected={currentUniqueIdentifierSelected}
					selectedGroup={selectedGroup}
				/>
			) : (
				<section className='card border-0 groups-empty-state mt-4'>
					<div className='card-body py-5'>
						<div className='groups-empty-state-layout'>
							<div>
								<p className='groups-page-eyebrow text-primary mb-2'>
									Primer paso
								</p>
								<h2 className='mb-3'>Todavia no tienes grupos</h2>
								<p className='text-muted mb-4'>
									Empieza creando tu propio grupo o unete con un codigo de
									invitacion para cargar pronosticos y seguir la tabla.
								</p>
								<div className='groups-empty-state-actions'>
									<Link to='/create-new-group' className='btn btn-primary'>
										Crear grupo
									</Link>
									<Link to='/groups/join' className='btn btn-outline-primary'>
										Unirme a un grupo
									</Link>
								</div>
							</div>
							<div className='groups-empty-state-guide'>
								<h3 className='groups-empty-state-guide-title mb-3'>
									Como funciona
								</h3>
								<div className='groups-empty-state-step'>
									<span className='groups-empty-state-step-number'>1</span>
									<span>Crea un grupo o recibe un codigo de invitacion.</span>
								</div>
								<div className='groups-empty-state-step'>
									<span className='groups-empty-state-step-number'>2</span>
									<span>Entra al grupo y revisa las reglas de apuesta.</span>
								</div>
								<div className='groups-empty-state-step'>
									<span className='groups-empty-state-step-number'>3</span>
									<span>Carga tus pronosticos y compite en la tabla.</span>
								</div>
							</div>
						</div>
					</div>
				</section>
			)}
		</div>
	);
}

export { SelectGroup };
